import { Factory } from 'fishery';

import { FactoryAutoRewindSequence } from './factory-auto-rewind-sequence';
import { paginationFactory } from './pagination.factories';
import { oneDayInMilliseconds } from '../data-contract/one-day-in-milliseconds';
import {
    hardSoftSkillProficiencyWithUnknownLevelSchema,
    languageSkillProficiencyWithUnknownLevelSchema,
} from '../data-contract/skill';
import { createUuidFromString } from '../uuid/uuid';

import type {
    BaseSkill,
    HardSoftSkill,
    HardSoftSkillProficiencyWithUnknownLevel,
    HardSoftSkillShort,
    HardSoftSkillWithIconColor,
    JobProfileSkill,
    LanguageSkill,
    LanguageSkillProficiencyWithUnknownLevel,
    LanguageSkillShort,
    LanguageSkillWithIconColor,
    PaginatedJobArchitectureSkill,
    Skill,
    SkillPreset,
    SkillShort,
    SkillsOptions,
} from '../data-contract/skill';
import type { RelatedJob } from '../data-contract/skill-details';

export const hardSoftSkillProficiencyLevelFactory =
    FactoryAutoRewindSequence.define<HardSoftSkillProficiencyWithUnknownLevel>(({ sequence }) =>
        hardSoftSkillProficiencyWithUnknownLevelSchema.parse(sequence % 5),
    );

export const languageSkillProficiencyLevelFactory =
    FactoryAutoRewindSequence.define<LanguageSkillProficiencyWithUnknownLevel>(({ sequence }) =>
        languageSkillProficiencyWithUnknownLevelSchema.parse(sequence % 6),
    );

export const skillShortFactory = FactoryAutoRewindSequence.define<SkillShort>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-short-skill-id-${sequence}`),
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const hardSoftSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`hard-soft-skill-id-${sequence}`),
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const hardSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`hard-skill-id-${sequence}`),
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: 'hard',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const softSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`soft-skill-id-${sequence}`),
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: 'soft',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const languageSkillShortFactory = FactoryAutoRewindSequence.define<LanguageSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0
            ? `Language Skill ${sequence}`
            : `Really Really Really Really long Language Skill ${sequence}`;

    return {
        id: createUuidFromString(`language-skill-id-${sequence}`),
        level: languageSkillProficiencyLevelFactory.build(),
        skillType: 'language',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const skillFactory = FactoryAutoRewindSequence.define<Skill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        description: `Description ${sequence}`,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const jobProfileSkillFactory = FactoryAutoRewindSequence.define<JobProfileSkill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        competencyId: `competency-id-${sequence % 3}`,
        description: `Description ${sequence}`,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

const createLanguage = (sequence: number): LanguageSkill => {
    const title =
        sequence % 3 !== 0
            ? `Language Skill ${sequence}`
            : `Really Really Really Really long Language Skill ${sequence}`;

    return {
        id: createUuidFromString(`language-skill-id-${sequence}`),
        level: languageSkillProficiencyLevelFactory.build(),
        skillType: 'language',
        title,
        titles: [{ language: 'en-US', title }],
    };
};

export const languageSkillFactory = FactoryAutoRewindSequence.define<LanguageSkill>(({ sequence }) =>
    createLanguage(sequence),
);

export const languageSkillWithIconColorFactory = FactoryAutoRewindSequence.define<LanguageSkillWithIconColor>(
    ({ sequence }) => {
        return {
            ...createLanguage(sequence),
            iconColor: '#ffffff',
        };
    },
);

const createHardSoftSkill = (sequence: number): HardSoftSkill => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: sequence % 2 !== 0 ? 'hard' : 'soft',
        title,
        titles: [{ language: 'en-US', title }],
    };
};

export const hardSoftSkillFactory = FactoryAutoRewindSequence.define<HardSoftSkill>(({ sequence }) => {
    return createHardSoftSkill(sequence);
});

export const hardSoftSkillWithIconColorFactory = FactoryAutoRewindSequence.define<HardSoftSkillWithIconColor>(
    ({ sequence }) => {
        return {
            ...createHardSoftSkill(sequence),
            iconColor: '#ffffff',
        };
    },
);

export const skillBasicFactory = FactoryAutoRewindSequence.define<BaseSkill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0
            ? `Related Skill ${sequence}`
            : `Really Really Really Really Really long Related Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const relatedJobFactory = FactoryAutoRewindSequence.define<RelatedJob, { readonly currentTimestamp: number }>(
    ({ sequence, transientParams: { currentTimestamp = Date.now() } }) => {
        const title =
            sequence % 3 !== 0
                ? `Related Job Skill ${sequence}`
                : `Really Really Really Really Really long Related Job Skill ${sequence}`;

        return {
            id: createUuidFromString(`related-job-skill-id-${sequence}`),
            publishedAt: new Date(currentTimestamp - sequence * oneDayInMilliseconds).toISOString(),
            skills: skillFactory.buildList((sequence % 5) + 1),
            title,
        };
    },
);

export const skillPresetFactory = FactoryAutoRewindSequence.define<SkillPreset>(({ sequence }) => ({
    hardSoftSkills: hardSoftSkillShortFactory.buildList(8),
    languages: languageSkillShortFactory.buildList(2),
    name: `Predefined Profile ${sequence}`,
}));

export const skillsOptionsFactory = Factory.define<SkillsOptions>(() => ({
    canDetectSkills: true,
    canReadRelatedSkills: true,
}));

export const paginatedJobArchitectureSkillFactory = FactoryAutoRewindSequence.define<PaginatedJobArchitectureSkill>(
    () => ({
        data: skillShortFactory.buildList(8),
        pagination: paginationFactory.build(),
    }),
);
